import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  TextField,
  CircularProgress,
  Paper,
  Alert,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { paymentProofsApi } from '../../../api/paymentProofs';

const getEstadoColor = (estado) => {
  const colors = {
    pendiente: 'warning',
    validado: 'success',
    rechazado: 'error',
  };
  return colors[estado] || 'default';
};

const getEstadoLabel = (estado) => {
  const labels = {
    pendiente: 'Pendiente',
    validado: 'Validado',
    rechazado: 'Rechazado',
  };
  return labels[estado] || estado;
};

export const PaymentProofDetail = ({ open, onClose, proof, onValidate, onReject, isLoading }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [editedAmount, setEditedAmount] = useState(null);

  useEffect(() => {
    if (open && proof) {
      loadImage();
      // Reset edited amount when proof changes
      const extractedData = proof.extracted_data || {};
      const monto = extractedData.monto || extractedData.amount || null;
      setEditedAmount(monto);
    } else {
      setImageUrl(null);
      setRejectReason('');
      setEditedAmount(null);
    }
  }, [open, proof]);

  const loadImage = async () => {
    if (!proof?.id) return;
    
    setImageLoading(true);
    try {
      // Use API function that sends headers properly
      const url = await paymentProofsApi.getPaymentProofImage(proof.id);
      setImageUrl(url);
    } catch (error) {
      console.error('Error loading image:', error);
      // Fallback to media_url if available
      if (proof.media_url) {
        setImageUrl(proof.media_url);
      }
    } finally {
      setImageLoading(false);
    }
  };

  const handleValidate = () => {
    // Prevent double-click: don't allow if already loading
    if (isLoading) {
      return;
    }
    
    // Don't allow validation if id_prestamo doesn't exist
    if (!proof.id_prestamo) {
      return;
    }
    // Include edited amount in extracted_data if it was changed
    const extractedData = proof.extracted_data || {};
    const originalAmount = extractedData.monto || extractedData.amount;
    const updatedExtractedData = editedAmount !== null && editedAmount !== originalAmount
      ? {
          ...extractedData,
          monto: editedAmount,
          amount: editedAmount,
        }
      : extractedData;
    
    onValidate(proof.id, {
      observaciones: proof.observaciones || '',
      extracted_data: updatedExtractedData,
    });
  };

  const handleReject = () => {
    if (!rejectReason.trim()) {
      alert('Por favor ingrese un motivo para rechazar el comprobante');
      return;
    }
    onReject(proof.id, rejectReason);
  };

  if (!proof) return null;

  const extractedData = proof.extracted_data || {};
  const monto = extractedData.monto || extractedData.amount || null;
  const fecha = extractedData.fecha || extractedData.date || null;
  const referencia = extractedData.referencia || extractedData.reference || null;
  const banco = extractedData.banco || extractedData.bank || null;
  const cuenta = extractedData.cuenta || extractedData.account || null;
  const notas = extractedData.notas || extractedData.notes || null;
  const editingConfidence = extractedData.editing_confidence || null;
  const appearsEdited = extractedData.appears_edited || false;
  const editingIndicators = extractedData.editing_indicators || [];
  
  // Check if high confidence editing detected
  const hasHighEditingConfidence = editingConfidence !== null && editingConfidence >= 0.9;
  
  // Check if duplicate detected
  const isDuplicate = proof.observaciones && proof.observaciones.includes('Ya existe un comprobante con los mismos datos');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            p: 1,
            borderRadius: 1,
            backgroundColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ReceiptIcon sx={{ fontSize: 20, color: 'white' }} />
        </Box>
        <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
          Detalles del Comprobante
        </Typography>
        <Chip
          label={getEstadoLabel(proof.estado)}
          color={getEstadoColor(proof.estado)}
          size="small"
          sx={{ fontWeight: 600 }}
        />
        <Button
          onClick={onClose}
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* High Confidence Editing Warning */}
        {hasHighEditingConfidence && (
          <Alert
            severity="error"
            icon={<WarningIcon />}
            sx={{
              mb: 3,
              fontSize: '1rem',
              fontWeight: 600,
              '& .MuiAlert-message': {
                width: '100%',
              },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              ⚠️ ADVERTENCIA: Imagen Posiblemente Editada
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Se detectó una alta probabilidad ({Math.round(editingConfidence * 100)}%) de que esta imagen haya sido editada o manipulada.
            </Typography>
            {editingIndicators && editingIndicators.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Indicadores detectados:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {editingIndicators.map((indicator, index) => (
                    <li key={index}>
                      <Typography variant="body2">{indicator}</Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              Por favor, revise cuidadosamente este comprobante antes de validarlo.
            </Typography>
          </Alert>
        )}
        
        {/* Duplicate Detection Warning */}
        {isDuplicate && (
          <Alert
            severity="error"
            icon={<WarningIcon />}
            sx={{
              mb: 3,
              fontSize: '1rem',
              fontWeight: 600,
              '& .MuiAlert-message': {
                width: '100%',
              },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              ⚠️ ADVERTENCIA: Comprobante Duplicado Detectado
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Ya existe un comprobante con los mismos datos en el sistema.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              Por favor, verifique que no sea un duplicado antes de validarlo.
            </Typography>
          </Alert>
        )}
        
        <Grid container spacing={3}>
          {/* Image Section */}
          <Grid item size={6}>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                maxWidth: { xs: '100%', sm: 380 },
                mx: 'auto',
                aspectRatio: '3 / 4',
                maxHeight: 'min(85vh, 720px)',
                backgroundColor: 'grey.50',
                overflow: 'hidden',
              }}
            >
              {imageLoading ? (
                <CircularProgress />
              ) : imageUrl ? (
                <img
                  src={imageUrl}
                  alt="Comprobante"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    display: 'block',
                  }}
                />
              ) : (
                <Typography color="text.secondary">Imagen no disponible</Typography>
              )}
            </Paper>
          </Grid>

          {/* Details Section */}
          <Grid item size={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Client Information */}
              {proof.cliente && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Cliente
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {proof.cliente.nombre || 'N/A'}
                  </Typography>
                  {proof.cliente.identificacion && (
                    <Typography variant="body2" color="text.secondary">
                      ID: {proof.cliente.identificacion}
                    </Typography>
                  )}
                </Box>
              )}

              <Divider />

              {/* Loan Information */}
              {proof.id_prestamo && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Préstamo
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {proof.id_prestamo || 'N/A'}
                  </Typography>
                  {proof.prestamo.monto_solicitado && (
                    <Typography variant="body2" color="text.secondary">
                      Monto solicitado: ${parseFloat(proof.prestamo.monto_solicitado).toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                    </Typography>
                  )}
                </Box>
              )}

              <Divider />

              {/* WhatsApp Number */}
              {proof.whatsapp_number && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Número WhatsApp
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {proof.whatsapp_number}
                  </Typography>
                </Box>
              )}

              <Divider />

              {/* Extracted Data */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Datos Extraídos
                </Typography>
                <Grid container spacing={0.5}>
                  {monto && (
                    <Grid item size={6} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                        Monto:
                      </Typography>
                      {proof.estado === 'pendiente' ? (
                        <TextField
                          type="number"
                          size="small"
                          fullWidth
                          value={editedAmount !== null ? editedAmount : monto}
                          onChange={(e) => {
                            const newAmount = parseFloat(e.target.value) || 0;
                            setEditedAmount(newAmount);
                          }}
                          inputProps={{
                            step: 0.01,
                            min: 0,
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              color: 'success.main',
                            },
                          }}
                          InputProps={{
                            startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>,
                          }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                          ${parseFloat(monto).toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                        </Typography>
                      )}
                    </Grid>
                  )}
                  {fecha && (
                    <Grid item size={6} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Fecha:
                      </Typography>
                      <Typography variant="body2">
                        {fecha}
                      </Typography>
                    </Grid>
                  )}
                  {referencia && (
                    <Grid item size={6} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Referencia:
                      </Typography>
                      <Typography variant="body2">
                        {referencia}
                      </Typography>
                    </Grid>
                  )}
                  {banco && (
                    <Grid item size={6} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Banco:
                      </Typography>
                      <Typography variant="body2">
                        {banco}
                      </Typography>
                    </Grid>
                  )}
                  {cuenta && (
                    <Grid item size={6} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Cuenta:
                      </Typography>
                      <Typography variant="body2">
                        {cuenta}
                      </Typography>
                    </Grid>
                  )}
                  {proof.confidence !== null && proof.confidence !== undefined && (
                    <Grid item size={6} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Nivel de Confianza:
                      </Typography>
                      <Typography variant="body2">
                        {(proof.confidence * 100).toFixed(0)}%
                      </Typography>
                    </Grid>
                  )}
                  {notas && (
                    <Grid item size={12} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Notas:
                      </Typography>
                      <Typography variant="body2">
                        {notas}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Divider />

              {/* Dates */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Fechas
                </Typography>
                {proof.created_at && (
                  <Typography variant="body2">
                    Creado: {new Date(proof.created_at).toLocaleString('es-ES')}
                  </Typography>
                )}
                {proof.updated_at && (
                  <Typography variant="body2">
                    Actualizado: {new Date(proof.updated_at).toLocaleString('es-ES')}
                  </Typography>
                )}
              </Box>

              {/* Observations */}
              {proof.observaciones && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Observaciones
                    </Typography>
                    <Typography variant="body2">
                      {proof.observaciones}
                    </Typography>
                  </Box>
                </>
              )}

              {/* Reject Reason Input */}
              {proof.estado === 'pendiente' && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Motivo de Rechazo (si aplica)
                    </Typography>
                    <TextField
                      fullWidth
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Ingrese el motivo del rechazo..."
                      size="small"
                    />
                  </Box>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2, px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Cerrar
        </Button>
        {proof.estado === 'pendiente' && (
          <>
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={handleValidate}
              disabled={isLoading || !proof.id_prestamo}
              color="success"
              sx={{ textTransform: 'none', fontWeight: 600,color: 'white' }}
              title={!proof.id_prestamo ? "No se puede validar sin ID de préstamo" : ""}
            >
              {isLoading ? 'Validando...' : 'Validar'}
            </Button>
            <Button
              variant="contained"
              startIcon={<CancelIcon />}
              onClick={handleReject}
              disabled={isLoading}
              color="error"
              sx={{ textTransform: 'none', fontWeight: 600,color: 'white' }}
            >
              {isLoading ? 'Rechazando...' : 'Rechazar'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

